import { useForm, Link } from '@inertiajs/react'
import { Page, PageHeader } from '@/components/page'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { useBasePath } from '@/lib/paths'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { InternationalPhoneInput } from '@/components/ui/international-phone-input'

type Props = {
  formation: { id: string; legal_name: string }
  countries: { code: string; name: string }[]
}

export default function PrincipalsNew({ formation, countries }: Props) {
  const basePath = useBasePath()
  const form = useForm({
    principal_kind: 'individual',
    full_name: '',
    legal_name: '',
    email: '',
    phone: '',
    country_code: 'US',
    state_province: '',
    city: '',
    postal_code: '',
    street_address_1: '',
    street_address_2: '',
    date_of_birth: '',
    tax_id_last4: ''
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.post(`${basePath}/formations/${formation.id}/principals`)
  }

  return (
    <DashboardLayout
      title="Add a person"
      breadcrumbs={[
        { label: 'Businesses', href: 'businesses' },
        { label: formation.legal_name, href: `formations/${formation.id}` },
        { label: 'People', href: `formations/${formation.id}/principals` },
        { label: 'Add a person' }
      ]}
    >
      <Page>
        <PageHeader
          title={<>Add a person</>}
          subtitle={<>Add an owner, director, or officer to {formation.legal_name}</>}
          actions={
            <form onSubmit={handleSubmit} className="space-y-6">
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Person type</h2>
                <RadioGroup
                  value={form.data.principal_kind}
                  onValueChange={(value) => form.setData('principal_kind', value)}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="individual" id="individual" />
                    <Label htmlFor="individual" className="text-sm font-normal">Individual</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="entity" id="entity" />
                    <Label htmlFor="entity" className="text-sm font-normal">Entity (Company)</Label>
                  </div>
                </RadioGroup>
              </section>

              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Name</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="full_name" className="text-sm font-normal text-muted-foreground">
                      {form.data.principal_kind === 'entity' ? 'Entity Legal Name' : 'Full Name'}
                    </Label>
                    <Input
                      id="full_name"
                      type="text"
                      value={form.data.full_name}
                      onChange={(e) => form.setData('full_name', e.target.value)}
                      placeholder={form.data.principal_kind === 'entity' ? 'Acme Inc.' : 'John Doe'}
                      required
                    />
                    {form.errors.full_name && <p className="text-[12px] text-destructive">{form.errors.full_name}</p>}
                  </div>
                  {form.data.principal_kind === 'entity' && (
                    <div className="space-y-1.5">
                      <Label htmlFor="legal_name" className="text-sm font-normal text-muted-foreground">Doing Business As (Optional)</Label>
                      <Input
                        id="legal_name"
                        type="text"
                        value={form.data.legal_name}
                        onChange={(e) => form.setData('legal_name', e.target.value)}
                        placeholder="Acme"
                      />
                    </div>
                  )}
                </div>
              </section>

              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Contact information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-normal text-muted-foreground">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={form.data.email}
                      onChange={(e) => form.setData('email', e.target.value)}
                      placeholder="john@example.com"
                    />
                    {form.errors.email && <p className="text-[12px] text-destructive">{form.errors.email}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-sm font-normal text-muted-foreground">Phone</Label>
                    <InternationalPhoneInput
                      id="phone"
                      value={form.data.phone}
                      onValueChange={(phone) => form.setData('phone', phone)}
                      defaultCountryCode={form.data.country_code}
                    />
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Address</h2>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="street_address_1" className="text-sm font-normal text-muted-foreground">Street Address</Label>
                    <Input
                      id="street_address_1"
                      type="text"
                      value={form.data.street_address_1}
                      onChange={(e) => form.setData('street_address_1', e.target.value)}
                      placeholder="123 Main St"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="street_address_2" className="text-sm font-normal text-muted-foreground">Apartment, Suite, etc. (Optional)</Label>
                    <Input
                      id="street_address_2"
                      type="text"
                      value={form.data.street_address_2}
                      onChange={(e) => form.setData('street_address_2', e.target.value)}
                      placeholder="Apt 4B"
                    />
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="col-span-2 space-y-1.5">
                      <Label htmlFor="city" className="text-sm font-normal text-muted-foreground">City</Label>
                      <Input
                        id="city"
                        type="text"
                        value={form.data.city}
                        onChange={(e) => form.setData('city', e.target.value)}
                        placeholder="San Francisco"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="state_province" className="text-sm font-normal text-muted-foreground">State</Label>
                      <Input
                        id="state_province"
                        type="text"
                        value={form.data.state_province}
                        onChange={(e) => form.setData('state_province', e.target.value)}
                        placeholder="CA"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="postal_code" className="text-sm font-normal text-muted-foreground">ZIP</Label>
                      <Input
                        id="postal_code"
                        type="text"
                        value={form.data.postal_code}
                        onChange={(e) => form.setData('postal_code', e.target.value)}
                        placeholder="94102"
                      />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="country_code" className="text-sm font-normal text-muted-foreground">Country</Label>
                    <CountryCombobox
                      id="country_code"
                      countries={countries}
                      value={form.data.country_code}
                      onValueChange={(countryCode) => form.setData('country_code', countryCode)}
                    />
                  </div>
                </div>
              </section>

              {form.data.principal_kind === 'individual' && (
                <section>
                  <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Personal information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="date_of_birth" className="text-sm font-normal text-muted-foreground">Date of Birth</Label>
                      <Input
                        id="date_of_birth"
                        type="date"
                        value={form.data.date_of_birth}
                        onChange={(e) => form.setData('date_of_birth', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="tax_id_last4" className="text-sm font-normal text-muted-foreground">SSN Last 4</Label>
                      <Input
                        id="tax_id_last4"
                        type="text"
                        maxLength={4}
                        value={form.data.tax_id_last4}
                        onChange={(e) => form.setData('tax_id_last4', e.target.value)}
                        placeholder="1234"
                      />
                    </div>
                  </div>
                </section>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" size="sm" asChild>
                  <Link href={`${basePath}/formations/${formation.id}/principals`}>Cancel</Link>
                </Button>
                <Button type="submit" size="sm" disabled={form.processing}>
                  {form.processing ? 'Adding...' : 'Add a person'}
                </Button>
              </div>
            </form>
          }
        />

    
      </Page>
    </DashboardLayout>
  )
}
